import { Add } from "@mui/icons-material"
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid"
import {
  AccreditationStatus,
  Customer,
  DocumentClassification,
  Examination,
  IssueStatus,
  Lead,
  Project,
  Report,
  ReportStatus,
  Status,
} from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { groupDevicesIntoTestSetups, TestSetup } from "utils/testSetupGrouping"
import { v4 } from "uuid"
import { getBusinessCustomerName } from "./ProjectViewsUtils"

type DesktopExaminationViewProps = {
  projects: Project[]
  selectedEmployeeId: string
  leads: Lead[]
  customers: Customer[]
}

// Hilfsfunktion um den Status-Text zu erhalten
const getStatusText = (status: Status): string => {
  switch (status) {
    case Status.OPEN:
      return "Offen"
    case Status.IN_PROGRESS:
      return "In Bearbeitung"
    case Status.DONE:
      return "Abgeschlossen"
    case Status.PAUSED:
      return "Pausiert"
    case Status.BILLED:
      return "Abgerechnet"
    default:
      return "Unbekannt"
  }
}

// Hilfsfunktion um die Status-Farbe zu erhalten
const getStatusColor = (
  status: Status
): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case Status.OPEN:
      return "default"
    case Status.IN_PROGRESS:
      return "primary"
    case Status.DONE:
      return "success"
    case Status.PAUSED:
      return "warning"
    case Status.BILLED:
      return "info"
    default:
      return "default"
  }
}

// Hilfsfunktion um den Fortschritt zu berechnen
const calculateProgress = (examination: Examination): number => {
  if (!examination.tasks || examination.tasks.length === 0) {
    return 0
  }

  const completedTasks = examination.tasks.filter((task) => task.status === Status.DONE).length
  return Math.round((completedTasks / examination.tasks.length) * 100)
}

// Hilfsfunktion um Datum in deutschem Format anzuzeigen
const formatGermanDateTime = (dateString: string | undefined): string => {
  if (!dateString) return "-"

  const date = new Date(dateString)
  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear()
  const hours = date.getHours().toString().padStart(2, "0")
  const minutes = date.getMinutes().toString().padStart(2, "0")

  return `${day}.${month}.${year} ${hours}:${minutes}`
}

// Hilfsfunktion um die Geräteliste kompakt zu formatieren
const formatDeviceList = (deviceNames: string[]): string => {
  if (!deviceNames || deviceNames.length === 0) {
    return ""
  }
  // Now the format is already simplified: "1x Device-123: DeviceName"
  return deviceNames.join(", ")
}

type allTestSetupsType = {
  id: string
  project: Project
  testSetup: TestSetup
  examination: Examination
  companyName: string
  examinationName: string
  status: Status
  progress: number
  deviceNames: string[]
}

export const DesktopExaminationView = ({
  projects,
  // selectedEmployeeId,
  leads,
  customers,
}: DesktopExaminationViewProps) => {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [activeTab, setActiveTab] = useState<number>(0)
  const { updateReport, fetchAllReportsBasicData } = useStoreActions((actions) => actions)
  const { reports } = useStoreState((state) => state)

  useEffect(() => {
    const ensureReports = async () => {
      try {
        await fetchAllReportsBasicData({ fresh: true })
      } catch (err) {
        console.error("DesktopExaminationView: Fehler beim Laden der Reports:", err)
        enqueueSnackbar("Fehler beim Laden der Reports. Bitte erneut versuchen.", {
          variant: "error",
        })
      }
    }

    ensureReports()
  }, [fetchAllReportsBasicData])

  // Alle TestSetups aus allen Projekten extrahieren
  const allTestSetups = useMemo(() => {
    const setups: Array<allTestSetupsType> = []

    projects.forEach((project) => {
      const { groupedDevices } = groupDevicesIntoTestSetups(project)
      const companyName = getBusinessCustomerName(
        project.lead.opportunityID || "",
        leads,
        customers
      )

      groupedDevices.forEach((group) => {
        group.testSetups.forEach((testSetup, testSetupIndex) => {
          // Jede Examination im TestSetup wird als separate Zeile angezeigt
          testSetup.examinations.forEach((examination) => {
            const deviceNames = testSetup.devices.map((device) => {
              const deviceCount = device.devices?.length || 1
              return `${deviceCount}x ${device.barcode}: ${device.name}`
            })

            setups.push({
              id: `${project.id}-${examination.id}-${testSetupIndex}`,
              project,
              testSetup,
              examination,
              companyName,
              examinationName: examination.name,
              status: examination.status,
              progress: calculateProgress(examination),
              deviceNames,
            })
          })
        })
      })
    })

    // TODO: TestSetups filtern nach selectedEmployeeId

    return setups
  }, [projects, leads, customers])

  // Gefilterte TestSetups basierend auf Status
  const filteredTestSetups = useMemo(() => {
    return allTestSetups.filter((testSetup) => {
      // Status-Filter
      if (statusFilter !== "all" && testSetup.status !== statusFilter) {
        return false
      }
      return true
    })
  }, [allTestSetups, statusFilter])

  // Handle examination row click
  const handleExaminationClick = useCallback(
    (examinationId: string, projectId: string) => {
      navigate(`/examination/${projectId}/${examinationId}`)
    },
    [navigate]
  )

  // Handle status filter change
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value)
  }

  // Handle create test report button click
  const handleCreateTestReport = useCallback(
    (projectId: string) => {
      const projectReports = reports.filter((report) => report.projectID === projectId)

      // Find the highest version number among existing reports
      let highestVersion = "1.0"

      if (projectReports.length > 0) {
        // Parse all version strings and find the highest
        const versions = projectReports.map((report) => {
          const version = report.version || "1.0"
          return { version }
        })

        // Find the highest version by comparing version strings
        const highestVersionData = versions.reduce((highest, current) => {
          // Convert version strings to numbers for comparison (e.g., "1.0" -> 1.0, "1.01" -> 1.01)
          const currentVersionNum = parseFloat(current.version) || 1.0
          const highestVersionNum = parseFloat(highest.version) || 1.0
          return currentVersionNum > highestVersionNum ? current : highest
        })

        highestVersion = highestVersionData.version
      }

      // Increment the version by 0.01
      const currentVersionNum = parseFloat(highestVersion) || 1.0
      const newVersionNum = currentVersionNum + 0.01
      const newVersion = newVersionNum.toFixed(2)

      // Create a new report with basic structure
      const newReportId = v4()
      const dateString = new Date().toLocaleDateString()
      const newReport: Report = {
        id: newReportId,
        name: `Test Report - ${dateString}`,
        title: `Test Report - ${dateString}`,
        documentNo: `TR-${Date.now()}`,
        version: newVersion,
        versionNumber: 1,
        isLatestVersion: true,
        createdAt: new Date().toISOString(),
        previousVersions: [],
        status: ReportStatus.DRAFT,
        info: "Test report generated from examination",
        reportURL: "",
        examinations: [],
        tester: [],
        chapter: [],
        projectID: projectId,
        // New fields for enhanced reporting
        classification: DocumentClassification.NOT_CLASSIFIED,
        issueStatus: IssueStatus.DRAFT,
        showDraftWatermark: true,
        accreditationStatus: AccreditationStatus.ACCREDITED,
        revisions: [],
        updatedAt: new Date().toISOString(),
        __typename: "Report",
      }

      // Add the report to the project
      updateReport({ projectID: projectId, report: newReport })

      // Navigate to the report page
      navigate(`/report/${projectId}/${newReportId}`)
    },
    [updateReport, navigate, reports]
  )

  // Handle report click for navigation
  const handleReportClick = useCallback(
    (projectId: string, reportId: string) => {
      navigate(`/report/${projectId}/${reportId}`)
    },
    [navigate]
  )

  // Calculate project progress based on examinations
  const calculateProjectProgress = useCallback((project: Project) => {
    const examinationStatusMap = new Map<string, Status>() // Map to track status of each unique examination

    // Collect all unique examinations and their statuses from all devices
    project.devices.forEach((device) => {
      if ("examinations" in device && Array.isArray(device.examinations)) {
        device.examinations.forEach((examination: Examination) => {
          // Only update if we haven't seen this examination before, or if the current status is more "complete"
          if (
            !examinationStatusMap.has(examination.id) ||
            examination.status === Status.DONE ||
            (examination.status === Status.IN_PROGRESS &&
              examinationStatusMap.get(examination.id) !== Status.DONE)
          ) {
            examinationStatusMap.set(examination.id, examination.status)
          }
        })
      }
    })

    // Count examinations by status
    let completedExaminations = 0
    let inProgressExaminations = 0
    let openExaminations = 0

    examinationStatusMap.forEach((status) => {
      if (status === Status.DONE) {
        completedExaminations++
      } else if (status === Status.IN_PROGRESS) {
        inProgressExaminations++
      } else {
        openExaminations++
      }
    })

    const totalExaminations = examinationStatusMap.size

    const result = {
      total: totalExaminations,
      completed: completedExaminations,
      inProgress: inProgressExaminations,
      open: openExaminations,
      percentage:
        totalExaminations > 0 ? Math.round((completedExaminations / totalExaminations) * 100) : 0,
    }

    return result
  }, [])

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  // Custom toolbar component for examinations
  const ExaminationsToolbar = () => (
    <GridToolbarContainer
      sx={{ p: 1, display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-end" }}
    >
      <Box sx={{ minWidth: 200 }}>
        <GridToolbarQuickFilter debounceMs={500} />
      </Box>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Status</InputLabel>
        <Select value={statusFilter} label="Status" onChange={handleStatusFilterChange}>
          <MenuItem value="all">Alle Status</MenuItem>
          <MenuItem value={Status.OPEN}>Offen</MenuItem>
          <MenuItem value={Status.IN_PROGRESS}>In Bearbeitung</MenuItem>
          <MenuItem value={Status.DONE}>Abgeschlossen</MenuItem>
          <MenuItem value={Status.PAUSED}>Pausiert</MenuItem>
          <MenuItem value={Status.BILLED}>Abgerechnet</MenuItem>
        </Select>
      </FormControl>
    </GridToolbarContainer>
  )

  // Custom toolbar component for test reports (only search)
  const TestReportsToolbar = () => (
    <GridToolbarContainer
      sx={{ p: 1, display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-end" }}
    >
      <Box sx={{ minWidth: 200 }}>
        <GridToolbarQuickFilter debounceMs={500} />
      </Box>
    </GridToolbarContainer>
  )

  const columns: GridColDef<allTestSetupsType>[] = [
    {
      field: "project",
      headerName: "ID",
      width: 100,
      sortable: true,
      valueGetter: (params: Project) => {
        return params.lead?.opportunityID || params.id
      },
    },
    {
      field: "companyName",
      headerName: "Firma",
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.companyName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.project.name}
          </Typography>
        </Box>
      ),
    },

    {
      field: "examinationName",
      headerName: "TestSetup",
      flex: 1.5,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.examination.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.examination.norm?.name || "Keine Norm"}
          </Typography>
          {params.row.deviceNames && params.row.deviceNames.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                ml: 1,

                lineHeight: 1.2,
                maxWidth: "100%",
                wordBreak: "break-word",
              }}
              title={formatDeviceList(params.row.deviceNames)}
            >
              Geräte: {formatDeviceList(params.row.deviceNames)}
            </Typography>
          )}
        </Box>
      ),
    },

    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={getStatusText(params.row.examination.status)}
          color={getStatusColor(params.row.examination.status)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "progress",
      headerName: "Fortschritt",
      width: 100,

      renderCell: (params) => (
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {params.row.progress}%
          </Typography>
        </Box>
      ),
    },
    // {
    //   field: "actions",
    //   headerName: "Aktionen",
    //   type: "actions",
    //   width: 120,
    //   getActions: (params) => [
    //     <GridActionsCellItem
    //       icon={
    //         <Tooltip title="Weitere Optionen">
    //           <MoreVertIcon />
    //         </Tooltip>
    //       }
    //       label="Weitere Optionen"
    //       onClick={() => {
    //         // TODO: Implementierung für weitere Optionen
    //         console.log("Weitere Optionen:", params.row.examination.id)
    //       }}
    //     />,
    //   ],
    // },
  ]

  return (
    <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            label={
              <Typography variant="body2" sx={{ pr: 1 }}>
                Prüfungen
              </Typography>
            }
          />
          <Tab
            label={
              <Typography variant="body2" sx={{ pr: 1 }}>
                Testberichte
              </Typography>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          <DataGrid
            rows={filteredTestSetups}
            columns={columns}
            pageSizeOptions={[10, 25, 50, 100]}
            onRowClick={(params) =>
              handleExaminationClick(params.row.examination.id, params.row.project.id)
            }
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
              sorting: {
                sortModel: [{ field: "companyName", sort: "asc" }],
              },
            }}
            slots={{
              toolbar: ExaminationsToolbar,
            }}
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "action.hover",
              },
            }}
            getRowHeight={() => 60}
          />
        </Box>
      )}

      {/* Testberichte Tab */}
      {activeTab === 1 && (
        <Box>
          <DataGrid
            rows={projects.map((project) => ({
              id: project.id,
              project,
              companyName: getBusinessCustomerName(
                project.lead.opportunityID || "",
                leads,
                customers
              ),
              projectName: project.name,
              progress: calculateProjectProgress(project),
              reports: (() => {
                const projectReports = reports.filter((report) => report.projectID === project.id)
                // Remove duplicates based on version number, keeping the most recent one for each version
                const versionMap = new Map()
                projectReports.forEach((report) => {
                  const version = report.version || "1.0"
                  const existing = versionMap.get(version)
                  if (
                    !existing ||
                    new Date(report.updatedAt || report.createdAt || 0).getTime() >
                      new Date(existing.updatedAt || existing.createdAt || 0).getTime()
                  ) {
                    versionMap.set(version, report)
                  }
                })
                // Sort by version number descending
                const uniqueReports = Array.from(versionMap.values())
                uniqueReports.sort((a, b) => {
                  // Parse version numbers for proper sorting (e.g., 1.10 > 1.9)
                  const parseVersion = (v: string) => {
                    const parts = (v || "1.0").split(".")
                    return parts.map((p: string) => parseInt(p, 10) || 0)
                  }
                  const vA = parseVersion(a.version)
                  const vB = parseVersion(b.version)
                  for (let i = 0; i < Math.max(vA.length, vB.length); i++) {
                    const diff = (vB[i] || 0) - (vA[i] || 0)
                    if (diff !== 0) return diff
                  }
                  return 0
                })
                return uniqueReports
              })(),
            }))}
            columns={[
              {
                field: "project",
                headerName: "ID",
                width: 100,
                sortable: true,
                valueGetter: (params: Project) => {
                  return params.lead?.opportunityID || params.id
                },
              },
              {
                field: "companyName",
                headerName: "Firma",
                flex: 1.5,
                sortable: true,
              },

              {
                field: "projectName",
                headerName: "Projekt",
                flex: 1.5,
                sortable: true,
              },
              {
                field: "progress",
                headerName: "Fortschritt",
                flex: 1,
                sortable: false,
                renderHeader: () => (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2" fontWeight="medium">
                      Fortschritt
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (offen/in Arbeit/fertig/gesamt)
                    </Typography>
                  </Box>
                ),
                renderCell: (params) => {
                  const progress = params.row.progress
                  return (
                    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {progress.percentage}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({progress.open}/{progress.inProgress}/{progress.completed}/
                          {progress.total})
                        </Typography>
                      </Box>
                    </Box>
                  )
                },
              },
              {
                field: "reports",
                headerName: "Testberichte",
                flex: 1.2,
                sortable: false,
                renderCell: (params) => {
                  if (params.row.reports.length === 0) {
                    return "-"
                  }

                  const reportOptions = params.row.reports.map((report) => ({
                    label: `V ${report.version || "1.0"}`,
                    value: report.id,
                    report: report,
                  }))

                  return (
                    <Box sx={{ py: 1, width: "100%" }}>
                      <Autocomplete
                        size="small"
                        options={reportOptions}
                        getOptionLabel={(option) => option.label}
                        value={null}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            handleReportClick(params.row.project.id, newValue.value)
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Version wählen..."
                            variant="outlined"
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {option.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.report.updatedAt
                                  ? formatGermanDateTime(option.report.updatedAt)
                                  : formatGermanDateTime(option.report.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        sx={{
                          "& .MuiAutocomplete-inputRoot": {
                            padding: "4px 8px",
                          },
                        }}
                      />
                    </Box>
                  )
                },
              },
              {
                field: "reportDates",
                headerName: "Letzte Änderung",
                flex: 1.3,
                minWidth: 200,
                sortable: false,
                renderCell: (params) => {
                  // Find the most recent report based on updatedAt or createdAt

                  if (params.row.reports.length === 0) {
                    return "-"
                  }

                  const mostRecentReport = params.row.reports.reduce((latest, current) => {
                    const latestDate = new Date(latest.updatedAt || latest.createdAt || 0)
                    const currentDate = new Date(current.updatedAt || current.createdAt || 0)
                    return currentDate > latestDate ? current : latest
                  })

                  return (
                    <Box sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.875rem",
                        }}
                      >
                        {mostRecentReport.updatedAt
                          ? formatGermanDateTime(mostRecentReport.updatedAt)
                          : formatGermanDateTime(mostRecentReport.createdAt)}
                      </Typography>
                    </Box>
                  )
                },
              },
              {
                field: "actions",
                headerName: "Aktionen",
                type: "actions",
                width: 120,
                getActions: (params) => [
                  <GridActionsCellItem
                    icon={
                      <Tooltip title="Testreport erstellen">
                        <Add />
                      </Tooltip>
                    }
                    label="Testreport erstellen"
                    onClick={() => {
                      handleCreateTestReport(params.row.project.id)
                    }}
                  />,
                ],
              },
            ]}
            getRowHeight={() => 60}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
              sorting: {
                sortModel: [{ field: "companyName", sort: "asc" }],
              },
            }}
            slots={{
              toolbar: TestReportsToolbar,
            }}
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "action.hover",
              },
            }}
          />
        </Box>
      )}
    </Box>
  )
}
